// Text catalog for the Tidsplanlægning / Scheduling service page.
// Icons live in src/pages/TidsplanlaegningPage.tsx and are aligned by index
// with the card arrays here.

import { type Locale } from "../config";
import { type PageMeta } from "../head";

type Prop = { title: string; text: string };
type Phase = { label: string; title: string; items: string[] };
type MethodCard = { title: string; text: string; bullets: string[] };
type Faq = { q: string; a: string };

type TidsplanlaegningStrings = {
  meta: PageMeta;
  keywords: string;
  ogTitle: string;
  ogDescription: string;
  twTitle: string;
  twDescription: string;
  hero: { eyebrow: string; title: string; body: string; ctaPrimary: string; ctaSecondary: string };
  intro: { eyebrow: string; heading: string; p1: string; p2: string };
  why: { eyebrow: string; heading: string; intro: string; props: Prop[] };
  approach: { eyebrow: string; heading: string; intro: string; phases: Phase[] };
  sectors: { eyebrow: string; heading: string; intro: string; items: string[] };
  methodology: { eyebrow: string; heading: string; intro: string; cards: MethodCard[] };
  challenges: { eyebrow: string; heading: string; intro: string; items: string[] };
  faq: { eyebrow: string; heading: string; items: Faq[] };
  cta: { heading: string; body: string; button: string };
  jsonLd: { serviceType: string; name: string; description: string; audienceType: string };
};

export const tidsplanlaegningStrings: Record<Locale, TidsplanlaegningStrings> = {
  da: {
    meta: {
      title: "Tidsplanlægning til byggeprojekter | MI Support",
      description:
        "Professionel tidsplanlægning til komplekse byggeprojekter. MI Support leverer erfarne tidsplanlæggere til pharma, food, biotech, energi og hospitalsbyggeri.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/Kontor-gruppe-15_1400x1400.jpg",
    },
    keywords:
      "tidsplanlægning, tidsplanlægger, WBS, project sequencing, project baseline, mitigation, Primavera P6, MS Project, Power BI, S-curve, progress reporting, pharma, biotech, hospitalsbyggeri",
    ogTitle: "Tidsplanlægning til byggeprojekter | MI Support",
    ogDescription:
      "Erfarne tidsplanlæggere der etablerer WBS, baseline og mitigation, fra design til handover. Til pharma, biotech, food og hospitalsbyggeri.",
    twTitle: "Tidsplanlægning | MI Support",
    twDescription:
      "Tidsplanlæggere der skaber transparens, baseline og mitigation, fra design til handover.",
    hero: {
      eyebrow: "TIDSPLANLÆGNING",
      title: "Tidsplanlægning fra design til handover",
      body: "MI Support stiller erfarne tidsplanlæggere til rådighed, som indgår direkte i jeres projektorganisation og leverer gennemarbejdede tidsplaner til komplekse projekter inden for pharma, food, biotech, hospitalsbyggeri og procesindustri.",
      ctaPrimary: "Tag en uforpligtende snak",
      ctaSecondary: "Se alle ydelser",
    },
    intro: {
      eyebrow: "Et aktivt styringsgrundlag",
      heading: "Tidsplanlægning er mere end en tidslinje",
      p1: "Tidsplanlægning hos MI Support handler ikke kun om at opstille en tidslinje. Det handler om at skabe et aktivt styringsgrundlag, der understøtter beslutningstagning, risikohåndtering og løbende optimering gennem hele projektets livscyklus. MI Supports tidsplanlæggere bygger sammenhæng mellem scope og tidsplan, fra tidlig konceptfase til aflevering.",
      p2: "Med over 25 års erfaring fra byggebranchen har vi dyb indsigt i kompleksiteten i industrielle byggeprojekter og de grænseflader, der skal håndteres for at sikre en stabil og kontrolleret byggeproces. Det gælder også også i compliance-drevne miljøer såsom pharma, biotech og procesindustri.",
    },
    why: {
      eyebrow: "Hvorfor MI Support",
      heading: "Tidsplaner I kan styre projektet efter",
      intro:
        "Vi bygger tidsplaner, der fungerer i praksis. Det gælder lige fra strategisk overblik til detaljeret fremdriftsstyring, så ledelsen har det rigtige beslutningsgrundlag på hvert niveau.",
      props: [
        {
          title: "Fuld kontrol over kritisk vej",
          text: "Vi kortlægger afhængigheder mellem projektering, indkøb, myndigheder, udførelse og commissioning, så I har overblik over den reelle kritiske vej og kan handle i tide.",
        },
        {
          title: "Datadrevet fremdriftsstyring",
          text: "Med struktureret fremdriftsrapportering får ledelsen et klart billede af status, risici og forventet aflevering, og vi sikrer, at tallene bag tidsplanen er gennemskuelige og kan bruges som beslutningsgrundlag.",
        },
        {
          title: "Stærke værktøjer og metoder",
          text: "Vores planlæggere arbejder i Primavera P6, MS Project og Power BI og kombinerer værktøjerne med en pragmatisk tilgang, der er tilpasset projektets størrelse, kompleksitet og de involverede parter.",
        },
      ],
    },
    approach: {
      eyebrow: "Vores tilgang",
      heading: "Tidsplanlægning på tre niveauer",
      intro:
        "Vi arbejder med tidsplanlægning som et integreret styringsværktøj fremfor en isoleret leverance. Vores planlæggere indgår i projektorganisationen og leverer planer på tre niveauer, der hænger sammen og understøtter både ledelses- og driftsbeslutninger.",
      phases: [
        {
          label: "Niveau 01",
          title: "Strategisk hovedtidsplan",
          items: [
            "Milestoneplan og overordnede faser",
            "Identifikation af kritisk vej og afhængigheder",
            "Scenariesimulering og 'what-if'-analyser",
            "Beslutningsgrundlag for ledelse og bestyrelse",
          ],
        },
        {
          label: "Niveau 02",
          title: "Koordineret detailtidsplan",
          items: [
            "Integreret plan på tværs af fag og entrepriser",
            "WBS, ressource- og leverancestyring",
            "Granskning af entreprenørernes tidsplaner",
            "Bindeled mellem projektering, udførelse og commissioning",
          ],
        },
        {
          label: "Niveau 03",
          title: "Fremdrift & rapportering",
          items: [
            "Ugentlig opfølgning og fremdriftsvurdering",
            "Forsinkelsesanalyser og krav-dokumentation",
            "Visuel rapportering til styregruppe og bygherre",
            "Korrigerende handlinger og recovery-planer",
          ],
        },
      ],
    },
    sectors: {
      eyebrow: "Brancher",
      heading: "Erfaring fra compliance-drevne byggerier",
      intro:
        "Vores tidsplanlæggere har erfaring fra både nybyggeri og renovering af laboratorier, procesanlæg, hospitaler, datacentre og kontorbyggeri med høje krav til dokumentation, kvalitet og myndighedsgodkendelser.",
      items: [
        "Pharma & biotech (GMP)",
        "Food & procesindustri",
        "Hospitalsbyggeri",
        "Datacentre & logistik",
        "Kontor- og hovedsædebyggeri",
        "Energi & infrastruktur",
      ],
    },
    methodology: {
      eyebrow: "Vores metode",
      heading: "Sådan arbejder MI Supports tidsplanlæggere",
      intro:
        "Vi kombinerer anerkendte planlægningsmetoder og digitale værktøjer, så projektets scope omsættes til en operationel og gennemførlig tidsplan med tydelige afhængigheder og klart definerede leverancer.",
      cards: [
        {
          title: "Work Breakdown Structure (WBS)",
          text: "Vores tidsplanlæggere nedbryder projektet i logiske arbejdspakker og delleverancer, der kan estimeres, planlægges og styres hver for sig i tæt dialog med bygherre, projekterende og entreprenører.",
          bullets: [
            "Entydig fordeling af scope, ansvar og grænseflader",
            "Implementeres direkte i Primavera P6 og MS Project",
            "Rygrad for aktiviteter, ressourcer og fremdrift",
          ],
        },
        {
          title: "Project Sequencing",
          text: "Med sekvensering fastlægger vi den rigtige rækkefølge af aktiviteter på tværs af projektering, indkøb, myndighedsbehandling, udførelse, commissioning og qualification.",
          bullets: [
            "Kortlægning af logiske og fysiske afhængigheder",
            "Identifikation og analyse af kritisk vej",
            "Scenarie- og what if-analyser af rækkefølge og ressourcetræk",
          ],
        },
        {
          title: "Project Baseline",
          text: "Baseline er den godkendte version af projektets omfang, tidsplan og budget og fungerer som det faste referencepunkt under hele projektet. Vi etablerer den sammen med bygherre og styregruppe og forankrer den i en formel ændringsproces.",
          bullets: [
            "Dokumentation af forudsætninger, antagelser og risici",
            "Formel proces for change management og baseline-revisioner",
            "Grundlag for fremdrifts- og forsinkelsesanalyser",
          ],
        },
        {
          title: "Mitigation",
          text: "Risici skal håndteres, før de bliver til forsinkelser. Vi integrerer risikoarbejdet direkte i tidsplanen og knytter konkrete mitigeringstiltag til aktiviteter og milepæle, så styregruppen får handlemuligheder i tide.",
          bullets: [
            "Bufferstrategier og scenarieplanlægning på kritisk vej",
            "Overvågning af lange leveringstider og ressourcekonflikter",
            "Recovery-planer ved skred i tidsplanen",
          ],
        },
        {
          title: "Progress Reporting",
          text: "Struktureret fremdriftsrapportering tilpasset både projektorganisation og styregruppe. Status, prognose og forventet aflevering er altid ét klik væk, og rapporteringen peger ikke kun på, hvor projektet er, men også hvad der skal handles på.",
          bullets: [
            "Ugentlige progress updates og stadeanalyser",
            "Trendanalyser og visuel fremdriftsrapportering",
            "Dashboards i Power BI eller bygherrens egne systemer",
          ],
        },
        {
          title: "Transparency",
          text: "Transparens er forudsætningen for tillid og hurtige beslutninger. Bygherre, rådgivere og entreprenører arbejder ud fra samme opdaterede styringsgrundlag, så diskussioner foregår på fakta fremfor fortolkninger.",
          bullets: [
            "Ensartede strukturer og klare statusdefinitioner",
            "Åben adgang til tidsplaner, S-curves og milepæle",
            "Synlig og objektiv dokumentation af afvigelser og ændringer",
          ],
        },
      ],
    },
    challenges: {
      eyebrow: "Udfordringer vi løser",
      heading: "Typiske problemstillinger tidsplanlægning afhjælper",
      intro:
        "Manglende eller utilstrækkelig tidsplanlægning er ofte en medvirkende årsag til problemer i komplekse byggeprojekter. En erfaren tidsplanlægger fra MI Support håndterer dem proaktivt.",
      items: [
        "Urealistiske tidsplaner uden hensyn til kompleksitet og afhængigheder",
        "Manglende overblik over kritiske aktiviteter og grænseflader",
        "Utilstrækkelig koordinering mellem bygherre, rådgivere og entreprenører",
        "Ressource- og kapacitetskonflikter, der bremser fremdriften",
        "Mangelfuld fremdriftsopfølgning og utilstrækkelig dokumentation",
        "Sen identifikation af risici og afvigelser fra baseline",
      ],
    },
    faq: {
      eyebrow: "FAQ",
      heading: "Ofte stillede spørgsmål om tidsplanlægning",
      items: [
        {
          q: "Hvad er forskellen på en hovedtidsplan og en detailtidsplan?",
          a: "Hovedtidsplanen er bygherrens overordnede styringsværktøj med milepæle, faser og kritisk vej. Detailtidsplanen er en koordineret plan på aktivitetsniveau på tværs af entrepriser, fag og leverancer. Begge skal hænge sammen, og det er præcis det, MI Supports tidsplanlæggere sikrer.",
        },
        {
          q: "Hvilke værktøjer bruger I til tidsplanlægning?",
          a: "Vi arbejder primært i Primavera P6 og Microsoft Project og rapporterer typisk via Power BI eller dashboards i bygherrens egne systemer.",
        },
        {
          q: "Hvornår bør vi koble en planlægger på projektet?",
          a: "Jo tidligere, jo bedre. Allerede i program- og projekteringsfasen sætter tidsplanen rammen for udbud, kontrakter og leverancestrategi. Vi kan dog også tage over på et igangværende projekt, for eksempel hvis tidsplanen er kommet under pres eller skal genoprettes.",
        },
        {
          q: "Kan I lave forsinkelsesanalyser og recovery-planer?",
          a: "Ja. Vi gennemfører forsinkelsesanalyser (time impact, as-planned vs. as-built) og udarbejder recovery-planer i samarbejde med entreprenørerne. Vores dokumentation kan også indgå som grundlag i krav- og tvistsager.",
        },
      ],
    },
    cta: {
      heading: "Skal vi tage hånd om jeres tidsplan?",
      body: "Står I overfor et stort byggeprojekt, eller er jeres nuværende tidsplan kommet under pres? MI Supports tidsplanlæggere er klar til at tage en uforpligtende snak om jeres situation og behov.",
      button: "Kontakt os og hør mere",
    },
    jsonLd: {
      serviceType: "Tidsplanlægning",
      name: "Tidsplanlægning og planlægningsledelse",
      description:
        "Professionel tidsplanlægning, planlægningsledelse og fremdriftsstyring til store og komplekse byggeprojekter, med Primavera P6 og MS Project.",
      audienceType: "Bygherrer, industri, life science, offentlige bygherrer",
    },
  },

  en: {
    meta: {
      title: "Scheduling for construction projects | MI Support",
      description:
        "Professional scheduling for complex construction projects. MI Support provides experienced schedulers for pharma, energy and hospital construction.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/Kontor-gruppe-15_1400x1400.jpg",
    },
    keywords:
      "scheduling, scheduler, WBS, project sequencing, project baseline, mitigation, Primavera P6, MS Project, Power BI, S-curve, progress reporting, pharma, biotech, hospital construction",
    ogTitle: "Scheduling for construction projects | MI Support",
    ogDescription:
      "Experienced schedulers who establish WBS, baseline and mitigation, from design to handover. For pharma, biotech, food and hospital construction.",
    twTitle: "Scheduling | MI Support",
    twDescription:
      "Schedulers who create transparency, baseline and mitigation, from design to handover.",
    hero: {
      eyebrow: "SCHEDULING",
      title: "Scheduling from design to handover",
      body: "MI Support provides experienced schedulers who integrate directly into your project organisation and deliver thoroughly worked-out schedules for complex projects within pharma, food, biotech, hospital construction and process industry.",
      ctaPrimary: "Have a no-obligation chat",
      ctaSecondary: "See all services",
    },
    intro: {
      eyebrow: "An active management basis",
      heading: "Scheduling is more than a timeline",
      p1: "Scheduling at MI Support is not just about setting up a timeline. It is about creating an active management basis that supports decision-making, risk management and ongoing optimisation throughout the project's entire life cycle. MI Support's schedulers build coherence between scope and schedule, from early concept phase to handover.",
      p2: "With over 25 years of experience from the construction industry, we have deep insight into the complexity of industrial construction projects and the interfaces that must be handled to ensure a stable and controlled construction process. This also applies in compliance-driven environments such as pharma, biotech and process industry.",
    },
    why: {
      eyebrow: "Why MI Support",
      heading: "Schedules you can steer the project by",
      intro:
        "We build schedules that work in practice. This applies from strategic overview to detailed progress management, so management has the right basis for decisions at every level.",
      props: [
        {
          title: "Full control of the critical path",
          text: "We map the dependencies between design, procurement, authorities, execution and commissioning, so you have an overview of the real critical path and can act in time.",
        },
        {
          title: "Data-driven progress management",
          text: "With structured progress reporting, management gets a clear picture of status, risks and expected handover, and we ensure that the figures behind the schedule are transparent and can be used as a basis for decisions.",
        },
        {
          title: "Strong tools and methods",
          text: "Our planners work in Primavera P6, MS Project and Power BI and combine the tools with a pragmatic approach tailored to the project's size, complexity and the parties involved.",
        },
      ],
    },
    approach: {
      eyebrow: "Our approach",
      heading: "Scheduling at three levels",
      intro:
        "We work with scheduling as an integrated management tool rather than an isolated deliverable. Our planners integrate into the project organisation and deliver plans at three levels that connect and support both management and operational decisions.",
      phases: [
        {
          label: "Level 01",
          title: "Strategic master schedule",
          items: [
            "Milestone plan and overall phases",
            "Identification of the critical path and dependencies",
            "Scenario simulation and 'what-if' analyses",
            "Decision basis for management and the board",
          ],
        },
        {
          label: "Level 02",
          title: "Coordinated detailed schedule",
          items: [
            "Integrated plan across disciplines and contracts",
            "WBS, resource and delivery management",
            "Review of the contractors' schedules",
            "Link between design, execution and commissioning",
          ],
        },
        {
          label: "Level 03",
          title: "Progress & reporting",
          items: [
            "Weekly follow-up and progress assessment",
            "Delay analyses and claim documentation",
            "Visual reporting to steering group and client",
            "Corrective actions and recovery plans",
          ],
        },
      ],
    },
    sectors: {
      eyebrow: "Industries",
      heading: "Experience from compliance-driven construction",
      intro:
        "Our schedulers have experience from both new construction and renovation of laboratories, process plants, hospitals, data centers and office buildings with high demands on documentation, quality and authority approvals.",
      items: [
        "Pharma & biotech (GMP)",
        "Food & process industry",
        "Hospital construction",
        "Data centers & logistics",
        "Office and headquarters construction",
        "Energy & infrastructure",
      ],
    },
    methodology: {
      eyebrow: "Our method",
      heading: "How MI Support's schedulers work",
      intro:
        "We combine recognised planning methods and digital tools, so the project's scope is translated into an operational and achievable schedule with clear dependencies and clearly defined deliverables.",
      cards: [
        {
          title: "Work Breakdown Structure (WBS)",
          text: "Our schedulers break the project down into logical work packages and sub-deliverables that can be estimated, planned and managed individually in close dialogue with the client, designers and contractors.",
          bullets: [
            "Unambiguous distribution of scope, responsibility and interfaces",
            "Implemented directly in Primavera P6 and MS Project",
            "Backbone for activities, resources and progress",
          ],
        },
        {
          title: "Project Sequencing",
          text: "With sequencing we determine the right order of activities across design, procurement, authority processing, execution, commissioning and qualification.",
          bullets: [
            "Mapping of logical and physical dependencies",
            "Identification and analysis of the critical path",
            "Scenario and what-if analyses of order and resource demand",
          ],
        },
        {
          title: "Project Baseline",
          text: "The baseline is the approved version of the project's scope, schedule and budget and acts as the fixed reference point throughout the entire project. We establish it together with the client and steering group and anchor it in a formal change process.",
          bullets: [
            "Documentation of prerequisites, assumptions and risks",
            "Formal process for change management and baseline revisions",
            "Basis for progress and delay analyses",
          ],
        },
        {
          title: "Mitigation",
          text: "Risks must be handled before they turn into delays. We integrate the risk work directly into the schedule and link concrete mitigation measures to activities and milestones, so the steering group gets options for action in time.",
          bullets: [
            "Buffer strategies and scenario planning on the critical path",
            "Monitoring of long lead times and resource conflicts",
            "Recovery plans in the event of schedule slippage",
          ],
        },
        {
          title: "Progress Reporting",
          text: "Structured progress reporting tailored to both project organisation and steering group. Status, forecast and expected handover are always one click away, and the reporting points not only to where the project is, but also what needs to be acted on.",
          bullets: [
            "Weekly progress updates and status analyses",
            "Trend analyses and visual progress reporting",
            "Dashboards in Power BI or the client's own systems",
          ],
        },
        {
          title: "Transparency",
          text: "Transparency is the prerequisite for trust and quick decisions. Client, advisors and contractors work from the same updated management basis, so discussions take place on facts rather than interpretations.",
          bullets: [
            "Uniform structures and clear status definitions",
            "Open access to schedules, S-curves and milestones",
            "Visible and objective documentation of deviations and changes",
          ],
        },
      ],
    },
    challenges: {
      eyebrow: "Challenges we solve",
      heading: "Typical issues scheduling remedies",
      intro:
        "Missing or inadequate scheduling is often a contributing cause of problems in complex construction projects. An experienced scheduler from MI Support handles them proactively.",
      items: [
        "Unrealistic schedules without regard to complexity and dependencies",
        "Lack of overview of critical activities and interfaces",
        "Inadequate coordination between client, advisors and contractors",
        "Resource and capacity conflicts that slow down progress",
        "Deficient progress follow-up and inadequate documentation",
        "Late identification of risks and deviations from baseline",
      ],
    },
    faq: {
      eyebrow: "FAQ",
      heading: "Frequently asked questions about scheduling",
      items: [
        {
          q: "What is the difference between a master schedule and a detailed schedule?",
          a: "The master schedule is the client's overall management tool with milestones, phases and critical path. The detailed schedule is a coordinated plan at activity level across contracts, disciplines and deliverables. Both must connect, and that is exactly what MI Support's schedulers ensure.",
        },
        {
          q: "Which tools do you use for scheduling?",
          a: "We work primarily in Primavera P6 and Microsoft Project and typically report via Power BI or dashboards in the client's own systems.",
        },
        {
          q: "When should we bring a planner onto the project?",
          a: "The earlier the better. Already in the programme and design phase, the schedule sets the framework for tendering, contracts and delivery strategy. However, we can also take over an ongoing project, for example if the schedule has come under pressure or needs to be restored.",
        },
        {
          q: "Can you do delay analyses and recovery plans?",
          a: "Yes. We carry out delay analyses (time impact, as-planned vs. as-built) and prepare recovery plans in collaboration with the contractors. Our documentation can also serve as a basis in claim and dispute cases.",
        },
      ],
    },
    cta: {
      heading: "Should we take care of your schedule?",
      body: "Are you facing a large construction project, or has your current schedule come under pressure? MI Support's schedulers are ready to have a no-obligation chat about your situation and needs.",
      button: "Contact us to learn more",
    },
    jsonLd: {
      serviceType: "Scheduling",
      name: "Scheduling and planning management",
      description:
        "Professional scheduling, planning management and progress management for large and complex construction projects, with Primavera P6 and MS Project.",
      audienceType: "Clients, industry, life science, public clients",
    },
  },

  de: {
    meta: {
      title: "Terminplanung für Bauprojekte | MI Support",
      description:
        "Professionelle Terminplanung für komplexe Bauprojekte. MI Support stellt erfahrene Terminplaner für Pharma, Food, Biotech, Energie und Krankenhausbau bereit.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/Kontor-gruppe-15_1400x1400.jpg",
    },
    keywords:
      "Terminplanung, Terminplaner, WBS, Project Sequencing, Project Baseline, Mitigation, Primavera P6, MS Project, Power BI, S-Curve, Progress Reporting, Pharma, Biotech, Krankenhausbau",
    ogTitle: "Terminplanung für Bauprojekte | MI Support",
    ogDescription:
      "Erfahrene Terminplaner, die WBS, Baseline und Mitigation etablieren – vom Design bis zur Übergabe. Für Pharma, Biotech, Food und Krankenhausbau.",
    twTitle: "Terminplanung | MI Support",
    twDescription:
      "Terminplaner, die Transparenz, Baseline und Mitigation schaffen – vom Design bis zur Übergabe.",
    hero: {
      eyebrow: "TERMINPLANUNG",
      title: "Terminplanung vom Design bis zur Übergabe",
      body: "MI Support stellt erfahrene Terminplaner bereit, die sich direkt in Ihre Projektorganisation einbinden und durchgearbeitete Terminpläne für komplexe Projekte in den Bereichen Pharma, Food, Biotech, Krankenhausbau und Prozessindustrie liefern.",
      ctaPrimary: "Unverbindliches Gespräch führen",
      ctaSecondary: "Alle Leistungen ansehen",
    },
    intro: {
      eyebrow: "Eine aktive Steuerungsgrundlage",
      heading: "Terminplanung ist mehr als eine Zeitachse",
      p1: "Terminplanung bei MI Support bedeutet nicht nur, eine Zeitachse aufzustellen. Es geht darum, eine aktive Steuerungsgrundlage zu schaffen, die Entscheidungsfindung, Risikomanagement und laufende Optimierung im gesamten Lebenszyklus des Projekts unterstützt. Die Terminplaner von MI Support schaffen Zusammenhang zwischen Scope und Terminplan – von der frühen Konzeptphase bis zur Übergabe.",
      p2: "Mit über 25 Jahren Erfahrung aus der Baubranche haben wir tiefen Einblick in die Komplexität industrieller Bauprojekte und in die Schnittstellen, die gehandhabt werden müssen, um einen stabilen und kontrollierten Bauprozess sicherzustellen. Das gilt auch in compliance-getriebenen Umgebungen wie Pharma, Biotech und Prozessindustrie.",
    },
    why: {
      eyebrow: "Warum MI Support",
      heading: "Terminpläne, nach denen Sie das Projekt steuern können",
      intro:
        "Wir erstellen Terminpläne, die in der Praxis funktionieren. Das gilt vom strategischen Überblick bis zur detaillierten Fortschrittssteuerung, sodass das Management auf jeder Ebene die richtige Entscheidungsgrundlage hat.",
      props: [
        {
          title: "Volle Kontrolle über den kritischen Pfad",
          text: "Wir kartieren die Abhängigkeiten zwischen Planung, Beschaffung, Behörden, Ausführung und Commissioning, sodass Sie einen Überblick über den tatsächlichen kritischen Pfad haben und rechtzeitig handeln können.",
        },
        {
          title: "Datengetriebene Fortschrittssteuerung",
          text: "Mit strukturierter Fortschrittsberichterstattung erhält das Management ein klares Bild von Status, Risiken und erwarteter Übergabe, und wir stellen sicher, dass die Zahlen hinter dem Terminplan nachvollziehbar sind und als Entscheidungsgrundlage genutzt werden können.",
        },
        {
          title: "Starke Werkzeuge und Methoden",
          text: "Unsere Planer arbeiten in Primavera P6, MS Project und Power BI und kombinieren die Werkzeuge mit einem pragmatischen Ansatz, der auf Größe, Komplexität und die beteiligten Parteien des Projekts abgestimmt ist.",
        },
      ],
    },
    approach: {
      eyebrow: "Unser Ansatz",
      heading: "Terminplanung auf drei Ebenen",
      intro:
        "Wir betrachten Terminplanung als integriertes Steuerungswerkzeug statt als isolierte Leistung. Unsere Planer binden sich in die Projektorganisation ein und liefern Pläne auf drei Ebenen, die zusammenhängen und sowohl Management- als auch Betriebsentscheidungen unterstützen.",
      phases: [
        {
          label: "Ebene 01",
          title: "Strategischer Hauptterminplan",
          items: [
            "Meilensteinplan und übergeordnete Phasen",
            "Identifikation des kritischen Pfads und der Abhängigkeiten",
            "Szenariosimulation und 'What-if'-Analysen",
            "Entscheidungsgrundlage für Management und Vorstand",
          ],
        },
        {
          label: "Ebene 02",
          title: "Koordinierter Detailterminplan",
          items: [
            "Integrierter Plan über Gewerke und Vergabeeinheiten hinweg",
            "WBS, Ressourcen- und Lieferungssteuerung",
            "Prüfung der Terminpläne der Auftragnehmer",
            "Bindeglied zwischen Planung, Ausführung und Commissioning",
          ],
        },
        {
          label: "Ebene 03",
          title: "Fortschritt & Berichtswesen",
          items: [
            "Wöchentliche Nachverfolgung und Fortschrittsbewertung",
            "Verzögerungsanalysen und Anspruchsdokumentation",
            "Visuelle Berichterstattung an Lenkungsausschuss und Bauherrn",
            "Korrekturmaßnahmen und Recovery-Pläne",
          ],
        },
      ],
    },
    sectors: {
      eyebrow: "Branchen",
      heading: "Erfahrung aus compliance-getriebenen Bauprojekten",
      intro:
        "Unsere Terminplaner haben Erfahrung sowohl aus Neubau als auch aus Sanierung von Laboren, Prozessanlagen, Krankenhäusern, Rechenzentren und Bürogebäuden mit hohen Anforderungen an Dokumentation, Qualität und Behördengenehmigungen.",
      items: [
        "Pharma & Biotech (GMP)",
        "Food & Prozessindustrie",
        "Krankenhausbau",
        "Rechenzentren & Logistik",
        "Büro- und Hauptsitzbau",
        "Energie & Infrastruktur",
      ],
    },
    methodology: {
      eyebrow: "Unsere Methode",
      heading: "So arbeiten die Terminplaner von MI Support",
      intro:
        "Wir kombinieren anerkannte Planungsmethoden und digitale Werkzeuge, sodass der Scope des Projekts in einen operativen und umsetzbaren Terminplan mit klaren Abhängigkeiten und klar definierten Leistungen überführt wird.",
      cards: [
        {
          title: "Work Breakdown Structure (WBS)",
          text: "Unsere Terminplaner zerlegen das Projekt in logische Arbeitspakete und Teilleistungen, die einzeln geschätzt, geplant und gesteuert werden können – in engem Dialog mit Bauherr, Planern und Auftragnehmern.",
          bullets: [
            "Eindeutige Verteilung von Scope, Verantwortung und Schnittstellen",
            "Direkt in Primavera P6 und MS Project implementiert",
            "Rückgrat für Aktivitäten, Ressourcen und Fortschritt",
          ],
        },
        {
          title: "Project Sequencing",
          text: "Mit der Sequenzierung legen wir die richtige Reihenfolge der Aktivitäten über Planung, Beschaffung, Behördenbearbeitung, Ausführung, Commissioning und Qualification hinweg fest.",
          bullets: [
            "Kartierung logischer und physischer Abhängigkeiten",
            "Identifikation und Analyse des kritischen Pfads",
            "Szenario- und What-if-Analysen von Reihenfolge und Ressourcenbedarf",
          ],
        },
        {
          title: "Project Baseline",
          text: "Die Baseline ist die genehmigte Version von Umfang, Terminplan und Budget des Projekts und dient während des gesamten Projekts als fester Referenzpunkt. Wir etablieren sie gemeinsam mit Bauherr und Lenkungsausschuss und verankern sie in einem formellen Änderungsprozess.",
          bullets: [
            "Dokumentation von Voraussetzungen, Annahmen und Risiken",
            "Formeller Prozess für Change Management und Baseline-Revisionen",
            "Grundlage für Fortschritts- und Verzögerungsanalysen",
          ],
        },
        {
          title: "Mitigation",
          text: "Risiken müssen gehandhabt werden, bevor sie zu Verzögerungen werden. Wir integrieren die Risikoarbeit direkt in den Terminplan und verknüpfen konkrete Mitigationsmaßnahmen mit Aktivitäten und Meilensteinen, sodass der Lenkungsausschuss rechtzeitig Handlungsmöglichkeiten erhält.",
          bullets: [
            "Pufferstrategien und Szenarioplanung auf dem kritischen Pfad",
            "Überwachung langer Lieferzeiten und Ressourcenkonflikte",
            "Recovery-Pläne bei Verzug im Terminplan",
          ],
        },
        {
          title: "Progress Reporting",
          text: "Strukturierte Fortschrittsberichterstattung, abgestimmt sowohl auf Projektorganisation als auch auf Lenkungsausschuss. Status, Prognose und erwartete Übergabe sind immer einen Klick entfernt, und die Berichterstattung zeigt nicht nur, wo das Projekt steht, sondern auch, worauf reagiert werden muss.",
          bullets: [
            "Wöchentliche Progress Updates und Standanalysen",
            "Trendanalysen und visuelle Fortschrittsberichterstattung",
            "Dashboards in Power BI oder den eigenen Systemen des Bauherrn",
          ],
        },
        {
          title: "Transparency",
          text: "Transparenz ist die Voraussetzung für Vertrauen und schnelle Entscheidungen. Bauherr, Berater und Auftragnehmer arbeiten auf derselben aktualisierten Steuerungsgrundlage, sodass Diskussionen auf Fakten statt auf Interpretationen geführt werden.",
          bullets: [
            "Einheitliche Strukturen und klare Statusdefinitionen",
            "Offener Zugang zu Terminplänen, S-Curves und Meilensteinen",
            "Sichtbare und objektive Dokumentation von Abweichungen und Änderungen",
          ],
        },
      ],
    },
    challenges: {
      eyebrow: "Herausforderungen, die wir lösen",
      heading: "Typische Problemstellungen, die Terminplanung behebt",
      intro:
        "Fehlende oder unzureichende Terminplanung ist oft eine mitwirkende Ursache für Probleme in komplexen Bauprojekten. Ein erfahrener Terminplaner von MI Support handhabt sie proaktiv.",
      items: [
        "Unrealistische Terminpläne ohne Rücksicht auf Komplexität und Abhängigkeiten",
        "Fehlender Überblick über kritische Aktivitäten und Schnittstellen",
        "Unzureichende Koordination zwischen Bauherr, Beratern und Auftragnehmern",
        "Ressourcen- und Kapazitätskonflikte, die den Fortschritt bremsen",
        "Mangelhafte Fortschrittsverfolgung und unzureichende Dokumentation",
        "Späte Identifikation von Risiken und Abweichungen von der Baseline",
      ],
    },
    faq: {
      eyebrow: "FAQ",
      heading: "Häufig gestellte Fragen zur Terminplanung",
      items: [
        {
          q: "Was ist der Unterschied zwischen einem Hauptterminplan und einem Detailterminplan?",
          a: "Der Hauptterminplan ist das übergeordnete Steuerungswerkzeug des Bauherrn mit Meilensteinen, Phasen und kritischem Pfad. Der Detailterminplan ist ein koordinierter Plan auf Aktivitätsebene über Vergabeeinheiten, Gewerke und Leistungen hinweg. Beide müssen zusammenhängen, und genau das stellen die Terminplaner von MI Support sicher.",
        },
        {
          q: "Welche Werkzeuge verwenden Sie für die Terminplanung?",
          a: "Wir arbeiten überwiegend in Primavera P6 und Microsoft Project und berichten in der Regel über Power BI oder Dashboards in den eigenen Systemen des Bauherrn.",
        },
        {
          q: "Wann sollten wir einen Planer in das Projekt einbinden?",
          a: "Je früher, desto besser. Bereits in der Programm- und Planungsphase setzt der Terminplan den Rahmen für Ausschreibung, Verträge und Lieferungsstrategie. Wir können jedoch auch ein laufendes Projekt übernehmen, zum Beispiel wenn der Terminplan unter Druck geraten ist oder wiederhergestellt werden muss.",
        },
        {
          q: "Können Sie Verzögerungsanalysen und Recovery-Pläne erstellen?",
          a: "Ja. Wir führen Verzögerungsanalysen (Time Impact, As-planned vs. As-built) durch und erstellen Recovery-Pläne in Zusammenarbeit mit den Auftragnehmern. Unsere Dokumentation kann auch als Grundlage in Anspruchs- und Streitfällen dienen.",
        },
      ],
    },
    cta: {
      heading: "Sollen wir uns um Ihren Terminplan kümmern?",
      body: "Stehen Sie vor einem großen Bauprojekt, oder ist Ihr aktueller Terminplan unter Druck geraten? Die Terminplaner von MI Support sind bereit, ein unverbindliches Gespräch über Ihre Situation und Ihre Bedürfnisse zu führen.",
      button: "Kontaktieren Sie uns für mehr Informationen",
    },
    jsonLd: {
      serviceType: "Terminplanung",
      name: "Terminplanung und Planungsleitung",
      description:
        "Professionelle Terminplanung, Planungsleitung und Fortschrittssteuerung für große und komplexe Bauprojekte, mit Primavera P6 und MS Project.",
      audienceType: "Bauherren, Industrie, Life Science, öffentliche Bauherren",
    },
  },
};
